import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    User,
    X,
    Upload,
    RefreshCw,
    Image as ImageIcon,
    Shapes,
    BadgeCheck,
    FileText,
    PenTool,
    Check,
    AlertCircle,
    CheckCircle2,
} from 'lucide-react';
import { getTagImageUrl, editModalAvatarShapeClass } from '@/utils/image-utils';
import { Input, Textarea } from '@/components/ui/Input';

export type ProfileSavingSection = null | 'data' | 'avatar' | 'banner' | 'moldura';

interface EditProfileModalProps {
    show: boolean;
    onClose: () => void;
    user: any;
    profileForm: any;
    setProfileForm: (form: any) => void;
    groupedTags: Record<string, any[]>;
    onSaveProfileData: () => void;
    onSaveAvatar: () => void;
    onSaveBanner: () => void;
    onSaveMoldura: () => void;
    savingSection: ProfileSavingSection;
    messages: { type: string; text: string };
    onOpenMolduras: () => void;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    bannerInputRef: React.RefObject<HTMLInputElement | null>;
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'banner') => void;
    onTagToggle: (tagId: number) => void;
    perfilStyle: 'default' | 'perfil2';
}

const sectionTitleClass =
    'flex items-center gap-2 text-[10px] font-black text-white/30 uppercase tracking-[0.25em]';
const fieldLabelClass =
    'flex items-center gap-2 text-[10px] font-black text-white/30 uppercase tracking-[0.25em] mb-3 group-focus-within:text-orange-500 transition-colors';
const saveBtnClass =
    'inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-orange-500/15 transition-all hover:from-orange-500 hover:to-orange-400 disabled:pointer-events-none disabled:opacity-45';

export function EditProfileModal({
    show,
    onClose,
    user,
    profileForm,
    setProfileForm,
    groupedTags,
    onSaveProfileData,
    onSaveAvatar,
    onSaveBanner,
    onSaveMoldura,
    savingSection,
    messages,
    onOpenMolduras,
    fileInputRef,
    bannerInputRef,
    onFileChange,
    onTagToggle,
    perfilStyle,
}: EditProfileModalProps) {
    const avatarShapeClass = editModalAvatarShapeClass(profileForm.molduraTipoPreview, perfilStyle);
    const busy = savingSection !== null;
    const initialMolId = user?.mol_id ?? null;
    const molduraDirty = profileForm.mol_id !== initialMolId;

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm shadow-2xl overscroll-behavior-contain"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-neutral-900 w-full max-w-lg rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                    >
                        <div className="relative h-28 bg-gradient-to-br from-orange-600/20 to-neutral-800 p-6 flex items-end justify-between overflow-hidden flex-shrink-0 md:h-32 md:p-8">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 blur-[60px] rounded-full -mr-10 -mt-10"></div>
                            <div className="z-10">
                                <h3 className="text-xl font-black text-white tracking-tight uppercase md:text-2xl">Editar Perfil</h3>
                                <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Avatar, banner, moldura e dados</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="absolute top-5 right-5 md:top-6 md:right-6 p-2.5 bg-white/5 hover:bg-white/10 rounded-full transition-all text-white/40 hover:text-white border border-white/10 group active:scale-90"
                            >
                                <X size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                        </div>

                        <div className="p-6 md:p-8 overflow-y-auto flex-1 min-h-0">
                            {messages.text && (
                                <div className={`text-[10px] font-black uppercase tracking-widest p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300 mb-6 ${messages.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'}`}>
                                    {messages.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
                                    {messages.text}
                                </div>
                            )}

                            <input
                                type="file"
                                ref={bannerInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => onFileChange(e, 'banner')}
                            />
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => onFileChange(e, 'avatar')}
                            />

                            <div className="divide-y divide-white/10">
                            {/* Avatar */}
                            <section className="space-y-4 pb-6">
                                <h4 className={sectionTitleClass}>
                                    <User size={12} /> Foto de perfil
                                </h4>
                                <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="relative shrink-0 group/avatar"
                                    >
                                        {profileForm.molduraPreview && (
                                            <div className="absolute -inset-3 pointer-events-none z-20">
                                                <img src={profileForm.molduraPreview} className="h-full w-full object-contain" alt="" />
                                            </div>
                                        )}
                                        <div
                                            className={`relative z-10 h-24 w-24 ${avatarShapeClass} overflow-hidden border-4 border-neutral-950 bg-neutral-900 shadow-xl transition-transform group-hover/avatar:scale-[1.02]`}
                                        >
                                            {profileForm.avatarPreview ? (
                                                <img src={profileForm.avatarPreview} className="h-full w-full object-cover" alt="" />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center text-white/15">
                                                    <User size={36} />
                                                </div>
                                            )}
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover/avatar:opacity-100">
                                                <Upload size={22} className="text-white" />
                                            </div>
                                        </div>
                                    </button>
                                    <div className="flex min-w-0 flex-1 flex-col gap-2 text-center sm:text-left">
                                        <p className="text-xs text-white/45">JPG ou PNG, máximo 5MB. A moldura escolhida abaixo aparece aqui como pré-visualização.</p>
                                        <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={busy}
                                                className="rounded-xl bg-white/5 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white/80 transition-all hover:bg-white/10 disabled:opacity-40"
                                            >
                                                Escolher
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onSaveAvatar}
                                                disabled={busy || !profileForm.avatarFile}
                                                className={saveBtnClass}
                                            >
                                                {savingSection === 'avatar' ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} strokeWidth={3} />}
                                                Salvar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Banner */}
                            <section className="space-y-4 py-6">
                                <h4 className={sectionTitleClass}>
                                    <ImageIcon size={12} /> Banner do perfil
                                </h4>
                                <button
                                    type="button"
                                    onClick={() => bannerInputRef.current?.click()}
                                    className="group/banner relative h-36 w-full overflow-hidden rounded-2xl bg-neutral-800 transition-opacity hover:opacity-95"
                                >
                                    {profileForm.bannerPreview ? (
                                        <img src={profileForm.bannerPreview} className="h-full w-full object-cover transition-transform duration-500 group-hover/banner:scale-105" alt="" />
                                    ) : (
                                        <div className="flex h-full w-full flex-col items-center justify-center text-white/12 group-hover/banner:text-orange-500/35">
                                            <ImageIcon size={40} strokeWidth={1} />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 backdrop-blur-[2px] transition-opacity group-hover/banner:opacity-100">
                                        <span className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold text-white shadow-lg">
                                            <Upload size={16} /> Alterar banner
                                        </span>
                                    </div>
                                </button>
                                <div className="flex flex-wrap justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => bannerInputRef.current?.click()}
                                        disabled={busy}
                                        className="rounded-xl bg-white/5 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white/80 transition-all hover:bg-white/10 disabled:opacity-40"
                                    >
                                        Escolher
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onSaveBanner}
                                        disabled={busy || !profileForm.bannerFile}
                                        className={saveBtnClass}
                                    >
                                        {savingSection === 'banner' ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} strokeWidth={3} />}
                                        Salvar
                                    </button>
                                </div>
                            </section>

                            {/* Moldura */}
                            <section className="space-y-4 py-6">
                                <h4 className={sectionTitleClass}>
                                    <Shapes size={12} /> Moldura
                                </h4>
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-neutral-800/80">
                                            {profileForm.molduraPreview ? (
                                                <img src={profileForm.molduraPreview} className="max-h-14 max-w-14 object-contain" alt="" />
                                            ) : (
                                                <span className="text-[10px] font-bold uppercase text-white/25">Nenhuma</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-white/45">Abra a galeria (paginada) e salve quando quiser.</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={onOpenMolduras}
                                            disabled={busy}
                                            className="rounded-xl bg-orange-500/15 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-orange-400 transition-all hover:bg-orange-500 hover:text-white disabled:opacity-40"
                                        >
                                            Galeria
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onSaveMoldura}
                                            disabled={busy || !molduraDirty}
                                            className={saveBtnClass}
                                        >
                                            {savingSection === 'moldura' ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} strokeWidth={3} />}
                                            Salvar
                                        </button>
                                    </div>
                                </div>
                            </section>

                            {/* Dados (nick, bio, tags) */}
                            <section className="space-y-6 pt-6">
                                <div className="group">
                                    <label className={fieldLabelClass}>
                                        <User size={12} /> Nome
                                    </label>
                                    <Input
                                        type="text"
                                        value={profileForm.nome}
                                        onChange={(e) => setProfileForm({ ...profileForm, nome: e.target.value })}
                                        className="rounded-2xl px-5 py-4 text-sm font-medium shadow-inner transition-all duration-300 focus:bg-neutral-800"
                                        placeholder="O seu nome"
                                        autoComplete="name"
                                    />
                                </div>
                                <div className="group">
                                    <label className={fieldLabelClass}>
                                        <PenTool size={12} /> Nickname
                                    </label>
                                    <Input
                                        type="text"
                                        value={profileForm.nick}
                                        onChange={(e) => setProfileForm({ ...profileForm, nick: e.target.value })}
                                        className="rounded-2xl px-5 py-4 text-sm font-medium shadow-inner transition-all duration-300 focus:bg-neutral-800"
                                        placeholder="Identificador único no site"
                                        autoComplete="username"
                                    />
                                </div>

                                <div className="group">
                                    <label className={fieldLabelClass}>
                                        <FileText size={12} /> Biografia / descrição
                                    </label>
                                    <Textarea
                                        value={profileForm.descricao}
                                        onChange={(e) => setProfileForm({ ...profileForm, descricao: e.target.value })}
                                        className="rounded-2xl px-5 py-4 text-sm font-medium shadow-inner transition-all duration-300 focus:bg-neutral-800 min-h-[120px] resize-none"
                                        placeholder="Conte um pouco sobre você..."
                                        rows={5}
                                    />
                                </div>

                                {user?.tags && user.tags.length > 0 && (
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-2 text-[10px] font-black text-white/30 uppercase tracking-[0.25em]">
                                            <BadgeCheck size={12} /> Tags em destaque
                                        </div>

                                        {Object.entries(groupedTags).map(([pacote, tags]: [string, any]) => (
                                            <div key={pacote} className="space-y-3">
                                                <h4 className="text-[9px] font-black text-orange-500/50 uppercase tracking-[0.2em] px-1">{pacote}</h4>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                    {tags.map((tag: any) => {
                                                        const isSelected = profileForm.selectedTags.includes(tag.tag_id);
                                                        return (
                                                            <button
                                                                key={tag.tag_id}
                                                                type="button"
                                                                onClick={() => onTagToggle(tag.tag_id)}
                                                                className={`relative group/tag h-14 rounded-2xl transition-all duration-300 flex items-center gap-3 px-3 border overflow-hidden ${isSelected
                                                                    ? 'border-orange-500/50 shadow-[0_0_20px_rgba(249,115,22,0.15)] bg-orange-500/5'
                                                                    : 'border-white/5 bg-white/5 hover:border-white/20'
                                                                    }`}
                                                            >
                                                                {isSelected && (
                                                                    <motion.div
                                                                        layoutId="tag-active-edit"
                                                                        className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-transparent pointer-events-none"
                                                                    />
                                                                )}

                                                                <div className="w-8 h-8 flex items-center justify-center relative z-10">
                                                                    {tag.tag_imagem ? (
                                                                        <img
                                                                            src={getTagImageUrl(tag.tag_imagem) || ''}
                                                                            alt={tag.tag_nome}
                                                                            className="w-full h-full object-contain transition-transform duration-300 group-hover/tag:scale-110"
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="w-3 h-3 rounded-full shadow-lg"
                                                                            style={{ backgroundColor: tag.tag_cor_fundo || '#444' }}
                                                                        />
                                                                    )}
                                                                </div>

                                                                <span className={`text-[11px] font-black uppercase tracking-wider truncate relative z-10 ${isSelected ? 'text-white' : 'text-white/40'}`}>
                                                                    {tag.tag_nome}
                                                                </span>

                                                                {isSelected && (
                                                                    <div className="ml-auto relative z-10 bg-orange-500 rounded-full p-0.5">
                                                                        <Check size={12} className="text-white" strokeWidth={3} />
                                                                    </div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-end pt-1">
                                    <button
                                        type="button"
                                        onClick={onSaveProfileData}
                                        disabled={busy}
                                        className={`${saveBtnClass} px-6 py-3`}
                                    >
                                        {savingSection === 'data' ? <RefreshCw size={16} className="animate-spin" /> : <Check size={14} strokeWidth={3} />}
                                        Salvar
                                    </button>
                                </div>
                            </section>
                            </div>
                        </div>

                        <div className="p-6 md:p-8 pt-4 border-t border-white/5 bg-neutral-900 flex-shrink-0">
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full py-3.5 bg-white/5 hover:bg-white/10 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] border border-white/5 transition-all duration-300 active:scale-[0.99]"
                            >
                                Fechar
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
